import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Loader2, Send, RotateCcw, RefreshCw, Landmark } from 'lucide-react';

// Admin Stripe Connect payouts — 2-step preview → send, plus history with a
// fraud-brake "reverse" while funds are still inside Stripe's hold window.
export default function StripePayoutsPanel({ getAuthHeader, apiUrl }) {
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [reversing, setReversing] = useState(null);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/admin/payouts/stripe/history`, { headers: getAuthHeader() });
      setHistory(res.data.payouts);
    } catch { /* silent */ }
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const runPreview = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${apiUrl}/api/admin/payouts/stripe/preview-batch`, {}, { headers: getAuthHeader() });
      setPreview(res.data);
      if (!res.data.eligible.length && !res.data.ineligible.length) toast.info('No members meet the $25 minimum yet');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const sendBatch = async () => {
    if (!preview?.eligible?.length) return;
    if (!window.confirm(`Send $${preview.total_amount.toFixed(2)} to ${preview.eligible.length} member(s) via Stripe?`)) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${apiUrl}/api/admin/payouts/stripe/send-batch`,
        { items: preview.eligible.map((e) => ({ user_id: e.user_id, amount: e.amount })) },
        { headers: getAuthHeader() },
      );
      toast.success(res.data.message);
      setPreview(null);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Batch failed');
    } finally {
      setSending(false);
    }
  };

  const reverse = async (p) => {
    if (!window.confirm(`Reverse the $${p.amount.toFixed(2)} payout to ${p.member_name || p.member_email}? Their platform balance will be restored.`)) return;
    setReversing(p.id);
    try {
      const res = await axios.post(`${apiUrl}/api/admin/payouts/stripe/reverse/${p.id}`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reverse failed');
    } finally {
      setReversing(null);
    }
  };

  return (
    <div className="wm-card p-6 rounded-lg space-y-6" data-testid="stripe-payouts-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-playfair font-bold text-foreground flex items-center gap-2">
            <Landmark className="w-5 h-5 text-gold" /> Stripe Connect Payouts
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Members onboard themselves through Stripe (bank + identity + tax handled by Stripe).
            Minimum payout $25. Funds sit in a 7-day Stripe hold before hitting their bank —
            reversible during that window if fraud is suspected.
          </p>
        </div>
        <Button onClick={runPreview} disabled={loading} variant="outline" className="border-border" data-testid="stripe-preview-batch-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Preview batch
        </Button>
      </div>

      {preview && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-foreground">
              <span className="text-emerald-400 font-semibold">{preview.eligible.length}</span> ready ·{' '}
              <span className="text-amber-400 font-semibold">{preview.ineligible.length}</span> blocked ·{' '}
              total <span className="text-gold font-semibold">${preview.total_amount.toFixed(2)}</span>
            </p>
            <Button onClick={sendBatch} disabled={sending || !preview.eligible.length} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="stripe-send-batch-btn">
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send {preview.eligible.length} payout(s)</>}
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2">Member</th><th className="py-2">Tier</th><th className="py-2">Amount</th><th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.eligible.map((e) => (
                  <tr key={e.user_id} className="border-b border-border/30" data-testid={`stripe-eligible-${e.user_id}`}>
                    <td className="py-2 text-foreground">{e.name} <span className="text-muted-foreground text-xs">{e.email}</span></td>
                    <td className="py-2 capitalize text-muted-foreground">{e.tier}</td>
                    <td className="py-2 text-emerald-400 font-medium">${e.amount.toFixed(2)}</td>
                    <td className="py-2"><span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">Ready</span></td>
                  </tr>
                ))}
                {preview.ineligible.map((e) => (
                  <tr key={e.user_id} className="border-b border-border/30 opacity-70" data-testid={`stripe-ineligible-${e.user_id}`}>
                    <td className="py-2 text-foreground">{e.name} <span className="text-muted-foreground text-xs">{e.email}</span></td>
                    <td className="py-2 capitalize text-muted-foreground">{e.tier}</td>
                    <td className="py-2 text-muted-foreground">${e.amount.toFixed(2)}</td>
                    <td className="py-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400">
                        {e.reason === 'no_stripe_account' ? 'No Stripe account' : 'Onboarding incomplete'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <h3 className="font-semibold text-foreground mb-3">Recent Stripe payouts</h3>
        {history.length === 0 ? (
          <p className="text-muted-foreground text-sm">No Stripe payouts sent yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2">Date</th><th className="py-2">Member</th><th className="py-2">Amount</th><th className="py-2">Status</th><th className="py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {history.map((p) => (
                  <tr key={p.id} className="border-b border-border/30" data-testid={`stripe-payout-row-${p.id}`}>
                    <td className="py-2 text-foreground whitespace-nowrap">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="py-2 text-foreground">{p.member_name} <span className="text-muted-foreground text-xs">{p.member_email}</span></td>
                    <td className="py-2 text-emerald-400 font-medium">${p.amount.toFixed(2)}</td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === 'paid' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>{p.status}</span>
                    </td>
                    <td className="py-2 text-right">
                      {p.status === 'paid' && (
                        <Button size="sm" variant="outline" disabled={reversing === p.id} onClick={() => reverse(p)} className="text-xs h-7 border-border" title="Reverse (fraud brake — works during the 7-day hold)" data-testid={`stripe-reverse-${p.id}`}>
                          <RotateCcw className="w-3 h-3 mr-1" /> Reverse
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
